/*
 * Copyright (c) 2026. Tooling Team
 */

import { Button } from "@uhg-abyss/web/ui/Button";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  LOINC_OPTIONS_LIMIT,
  MAPPING_STATUS_OPTIONS,
} from "../utils/constants";
import { LoincValuesContext } from "../utils/contexts";

const BULK_EDIT_MODAL_FORM_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

const BULK_EDIT_MODAL_FOOTER_STYLE = {
  display: "flex",
  gap: "8px",
};

const UNCHANGED = "UNCHANGED";
const UNCHANGED_OPTION = {
  value: UNCHANGED,
  label: UNCHANGED,
};

const hasPayloadValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const getLoincConceptCode = (row) =>
  row?.loincConceptCode || row?.loincConcept?.code || "";

const getLoincConceptDescription = (row) =>
  row?.loincConceptDescription || row?.loincConcept?.description || "";

const getInitialValues = (selectedRows) => {
  if (selectedRows.length !== 1) {
    return {
      code: UNCHANGED,
      description: "",
      status: UNCHANGED,
      userComment: UNCHANGED,
    };
  }

  const row = selectedRows[0];
  return {
    code: getLoincConceptCode(row),
    description: getLoincConceptDescription(row),
    status: row?.status || "",
    userComment: row?.userComments ?? "",
  };
};

const BulkEditModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedRows,
  dataTableProps,
  LAB_DOMAIN_ROW_ID,
}) => {
  const loincValuesRef = useContext(LoincValuesContext);
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("");
  const [userComment, setUserComment] = useState("");
  const [initialValues, setInitialValues] = useState(() =>
    getInitialValues(selectedRows)
  );
  const [isCodeTouched, setIsCodeTouched] = useState(false);
  const [isStatusTouched, setIsStatusTouched] = useState(false);
  const [isUserCommentTouched, setIsUserCommentTouched] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const isBulkEdit = selectedRows.length > 1;
  const selectedRowsRef = useRef(selectedRows);
  const selectedRowsSignature = selectedRows
    .map((row) => row?.[LAB_DOMAIN_ROW_ID] ?? row?._id ?? "")
    .join("|");

  selectedRowsRef.current = selectedRows;

  // Set selected rows into edit mode when modal opens; clear on close
  useEffect(() => {
    if (!dataTableProps || !selectedRows.length) return;
    const editModeMap = selectedRows.reduce((acc, row) => {
      acc[row[LAB_DOMAIN_ROW_ID]] = isOpen;
      return acc;
    }, {});
    dataTableProps.editRowState.setRowsInEditMode(editModeMap);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const nextInitialValues = getInitialValues(selectedRowsRef.current);
      setInitialValues(nextInitialValues);
      setCode(nextInitialValues.code);
      setDescription(nextInitialValues.description);
      setStatus(nextInitialValues.status);
      setUserComment(nextInitialValues.userComment);
      setIsCodeTouched(false);
      setIsStatusTouched(false);
      setIsUserCommentTouched(false);
      setSearchInput("");
      setDebouncedSearch("");
    }
  }, [isOpen, selectedRowsSignature]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Mirror modal form values into Abyss temp edited data for all selected rows
  useEffect(() => {
    if (!isOpen || !dataTableProps) return;

    const editActions = dataTableProps.table?.options?.meta?.editActions;
    if (!editActions?.updateTempData) return;

    // Build rowId → TanStack row index map from the core (unpaginated) row model
    const allRows = dataTableProps.table?.getCoreRowModel()?.rows ?? [];
    const selectedIds = new Set(selectedRows.map((r) => r[LAB_DOMAIN_ROW_ID]));
    const tanstackRows = allRows.filter((r) => selectedIds.has(r.id));
    const codeChanged =
      isCodeTouched && code !== UNCHANGED && hasPayloadValue(code);
    const statusChanged =
      isStatusTouched && status !== UNCHANGED && hasPayloadValue(status);
    const userCommentChanged =
      isUserCommentTouched && userComment !== UNCHANGED;

    tanstackRows.forEach((row) => {
      if (codeChanged) {
        editActions.updateTempData(row.index, "loincConceptCode", code);
        editActions.updateTempData(
          row.index,
          "loincConceptDescription",
          description
        );
      }
      if (statusChanged) {
        editActions.updateTempData(row.index, "status", status);
      }
      if (userCommentChanged) {
        editActions.updateTempData(row.index, "userComments", userComment);
      }
    });

    // Re-call setRowsInEditMode to trigger a re-render of cells that read
    // rowsInEditMode (like LoincDescriptionCell) so they pick up the updated
    // temp data written above. EditableTableCell-based cells are notified
    // directly by updateTempData; plain cells are not.
    const editModeMap = selectedRows.reduce((acc, row) => {
      acc[row[LAB_DOMAIN_ROW_ID]] = isOpen;
      return acc;
    }, {});
    dataTableProps.editRowState.setRowsInEditMode(editModeMap);
  }, [
    isOpen,
    code,
    description,
    status,
    userComment,
    isCodeTouched,
    isStatusTouched,
    isUserCommentTouched,
  ]);

  const loincValues = loincValuesRef?.current ?? [];

  const loincOptions = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const matchingValues =
      term.length <= 3
        ? loincValues
        : loincValues.filter(
            ({ code, description }) =>
              code?.toLowerCase().includes(term) ||
              description?.toLowerCase().includes(term)
          );

    let options = matchingValues
      .slice(0, LOINC_OPTIONS_LIMIT)
      .map(({ code, description }) => ({
        value: code,
        label: description ? `${code} \u2013 ${description}` : code,
      }));

    if (
      code &&
      code !== UNCHANGED &&
      !options.some((option) => option.value === code)
    ) {
      options = [
        {
          value: code,
          label: description ? `${code} \u2013 ${description}` : code,
        },
        ...options,
      ];
    }

    if (isBulkEdit) {
      return [UNCHANGED_OPTION, ...options];
    }

    return options;
  }, [loincValues, debouncedSearch, isBulkEdit, code, description]);

  const statusOptions = useMemo(
    () =>
      isBulkEdit
        ? [UNCHANGED_OPTION, ...MAPPING_STATUS_OPTIONS]
        : MAPPING_STATUS_OPTIONS,
    [isBulkEdit]
  );

  const shouldSendCode =
    isCodeTouched &&
    code !== UNCHANGED &&
    hasPayloadValue(code) &&
    code !== initialValues.code;
  const shouldSendStatus =
    isStatusTouched &&
    status !== UNCHANGED &&
    hasPayloadValue(status) &&
    status !== initialValues.status;
  const shouldSendUserComment =
    isUserCommentTouched &&
    userComment !== UNCHANGED &&
    userComment !== initialValues.userComment;
  const hasChanges = Boolean(
    shouldSendCode || shouldSendStatus || shouldSendUserComment
  );

  const handleCodeChange = (selectedCode) => {
    setIsCodeTouched(true);
    setCode(selectedCode);
    if (selectedCode === UNCHANGED || !selectedCode) {
      setDescription("");
      return;
    }
    const match = loincValues.find(({ code }) => code === selectedCode);
    setDescription(match?.description ?? "");
  };

  const handleStatusChange = (selectedStatus) => {
    setIsStatusTouched(true);
    setStatus(selectedStatus);
  };

  const handleConfirm = () => {
    if (!hasChanges) return;

    const body = selectedRows.map((row) => {
      const fields = { _id: row._id };
      if (shouldSendCode) fields.loincCode = code;
      if (shouldSendStatus) fields.status = status;
      if (shouldSendUserComment) fields.userComment = userComment;
      return fields;
    });
    onConfirm({
      body,
      ...(shouldSendCode && { loincConceptDescription: description }),
    });
  };

  const footer = (
    <div style={BULK_EDIT_MODAL_FOOTER_STYLE}>
      <Button isDisabled={!hasChanges} onClick={handleConfirm}>
        Confirm
      </Button>
      <Button variant="outline" onClick={onClose}>
        Dismiss
      </Button>
    </div>
  );

  return (
    <ModalDialog
      header={`Edit ${selectedRows.length} Row${
        selectedRows.length !== 1 ? "s" : ""
      }`}
      isOpen={isOpen}
      onClose={onClose}
      footer={footer}
    >
      <div style={BULK_EDIT_MODAL_FORM_STYLE}>
        <SelectInput
          label="Code"
          placeholder="Type to search..."
          isSearchable
          isClearable
          value={code}
          onChange={handleCodeChange}
          onInputChange={setSearchInput}
          options={loincOptions}
        />
        <TextInput label="Description" value={description} isDisabled />
        <SelectInput
          label="Status"
          placeholder="Select..."
          isClearable
          value={status}
          onChange={handleStatusChange}
          options={statusOptions}
        />
        <TextInput
          label="User Comment"
          value={userComment}
          onChange={(e) => {
            setIsUserCommentTouched(true);
            setUserComment(e.target.value);
          }}
        />
      </div>
    </ModalDialog>
  );
};

BulkEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  dataTableProps: PropTypes.object,
  LAB_DOMAIN_ROW_ID: PropTypes.string,
};

export default BulkEditModal;
